import { useTranslation } from "react-i18next";
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge, Button, ButtonGroup, Col, Dropdown, FormGroup, FormLabel, Row } from 'react-bootstrap';
import FilterAccordion from "../../../Common/Filter/FilterAccordion";
import BranchSelect from "../../../Custom/Main/Branch/BranchSelect";
import InputDate from "../../../Forms/Element/InputDate";
import YesNoSelect from "../../../Common/YesNoSelect";
import { errorObjectAlert, warnAlert } from "../../../alert/defaultAlert";
import { SearchOperation } from "../../../../../utils/ApiUtils";
import axiosInstance from "../../../../../services/AxiosInstance";
import Api from "../../../../../constants/Api";
import GridCustomBase from "../../../GridCustom/GridCustomBase";
import { formatDateTime, formatDateToYYYYMMDD } from "../../../../../utils/DateUtils";
import Common from "../../../../../constants/Common";
import InputText from "../../../Forms/Element/InputText";
import { convertDoubleToStringPTBR } from "../../../../../utils/ConverterUtils";
import BanIcon from "../../../Icon/BanIcon";
import ReasonSelect from "../Reason/ReasonSelect";
import ModalPageEvent from "../../../Common/Modal/ModalPageEvent";
import ModalPage from "../../../Common/Modal/ModalPage";
import SearchIcon from "../../../Icon/SearchIcon";
import { useNavigate } from "react-router-dom";
import MenuConfig from "../../../../../constants/MenuConfig";
import SaveIcon from "../../../Icon/SaveIcon";
import NonTaxCouponGenerator from "./NonTaxCouponGenerator";
import { useBranch } from "../../../../../context/BranchContext";
import OrderDetail from "./OrderDetail";
import PaymentMethodSelect from "../../Common/PaymentMethod/PaymentMethodSelect";
import CategoryMultiSelect from "../../Main/Category/CategoryMultiSelect";
import EmployeeSelect from "../../Main/Employee/EmployeeSelect";
import "./OrderHistory.css";

// Status que não devem permitir cancelamento direto da venda porque já houve reconhecimento financeiro.
const OrderHistory = () => {

    const { t } = useTranslation();
    const { selectedBranch } = useBranch();
    const navigate = useNavigate();

    // Estados do filtro
    const [startDate, setStartDate] = useState(new Date());
    const [endDate, setEndDate] = useState(new Date());
    const [customer, setCustomer] = useState('');
    // Nasce em "Não" para a tela abrir só com as vendas que valem dinheiro, o mesmo
    // recorte do dashboard, do extrato do colaborador e da API de integração. Quem
    // procura uma comanda cancelada limpa o filtro e ela volta para a lista.
    const [canceledValue, setCanceledValue] = useState('N');
    const [orderNumber, setOrderNumber] = useState('');
    const [branch, setBranch] = useState(null);
    const [paymentMethod, setPaymentMethod] = useState(null);
    const [productCategories, setProductCategories] = useState([]);
    const [employeeFilter, setEmployeeFilter] = useState(null);

    // O label acompanha o idioma; guardar o objeto no estado congelaria o texto
    // traduzido no momento em que o filtro foi escolhido.
    const canceled = useMemo(
        () => (canceledValue ? { value: canceledValue, label: t(canceledValue === 'N' ? 'no' : 'yes') } : null),
        [canceledValue, t]);

    // Estados da grid
    const [pageable, setPageable] = useState(null);
    const [page, setPage] = useState(0);
    const [rows, setRows] = useState([]);
    const [size, setSize] = useState(Common.DEFAULT_ROWS_PER_PAGE);
    const [showLoading, setShowLoading] = useState(false);
    const [selectedRows, setSelectedRows] = useState([]);

    // Estados de modais
    const [reasonCancellation, setReasonCancellation] = useState(null);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [showReasonModal, setShowReasonModal] = useState(false);
    const [showNonTaxCoupon, setShowNonTaxCoupon] = useState(false);
    const [showOrderDetail, setShowOrderDetail] = useState(false);

    // Mantém em memória os pedidos selecionados para validar regras de ação sem depender apenas da UI visual.
    const selectedOrderRows = rows.filter((row) => selectedRows.includes(String(row.id)) || selectedRows.includes(row.id));

    const checkboxFun = (type) => {
        setTimeout(() => {
            const checkboxes = document.querySelectorAll('.item-history input');
            const motherCheckBox = document.querySelector('.sorting_asc input');
            let selectedRows = [];
        
            if (type === 'all') {
                // Marca ou desmarca todos os checkboxes conforme o estado do motherCheckBox
                checkboxes.forEach(element => {
                    element.checked = motherCheckBox.checked;
                });
            } else {
                // Verifica se todos os checkboxes individuais estão marcados
                let allChecked = true;
        
                checkboxes.forEach(element => {
                    if (!element.checked) {
                        allChecked = false;
                    }
                });
        
                // Atualiza o estado do motherCheckBox corretamente
                motherCheckBox.checked = allChecked;
            }
        
            // Atualiza a lista de selecionados
            checkboxes.forEach(element => {
                if (element.checked) {
                    selectedRows.push(element.value);
                }
            });
        
            setSelectedRows(selectedRows);
        }, 200);
    }

    // Função de busca otimizada
    const search = useCallback((targetPage = page, targetSize = size) => {
        // Validações
        if (!startDate) {
            warnAlert(t('validation.required.start_date'));
            return;
        }
        if (!endDate) {
            warnAlert(t('validation.required.end_date'));
            return;
        }

        const filters = [];

        // Filtro de data
        filters.push(
            {
                field: "requestDate",
                value: formatDateToYYYYMMDD(startDate) + "T00:00:00",
                operation: SearchOperation.GREATER_THAN_EQUAL
            },
            {
                field: "requestDate",
                value: formatDateToYYYYMMDD(endDate) + "T23:59:59",
                operation: SearchOperation.LESS_THAN_EQUAL
            }
        );

        // Filtro de filial (contexto tem prioridade sobre seleção manual)
        if (selectedBranch !== null) {
            filters.push({
                field: "branch.id",
                value: selectedBranch.id,
                operation: SearchOperation.EQUAL
            });
        } else if (branch) {
            filters.push({
                field: "branch.id",
                value: branch.value,
                operation: SearchOperation.EQUAL
            });
        }

        // Filtro de cliente
        if (customer) {
            filters.push({
                field: "customer.fullName",
                value: customer,
                operation: SearchOperation.MATCH
            });
        }

        // Filtro de número do pedido
        if (orderNumber) {
            filters.push({
                field: "number",
                value: orderNumber,
                operation: SearchOperation.EQUAL
            });
        }

        if (paymentMethod) {
            filters.push({
                field: "paymentMethod",
                value: paymentMethod.value,
                operation: SearchOperation.EQUAL
            });
        }

        if (employeeFilter) {
            filters.push({
                field: "employee.id",
                value: employeeFilter.value,
                operation: SearchOperation.EQUAL
            });
        }

        if (productCategories && productCategories.length > 0) {
            filters.push({
                field: "productCategoryIds",
                value: productCategories.map((category) => category.value),
                operation: SearchOperation.IN
            });
        }

        // Filtro de cancelamento pela data, e não pelo motivo: é o campo que o dashboard,
        // o extrato do colaborador e a API de integração usam para excluir venda cancelada.
        // Os dois são gravados juntos no cancelamento, mas com o filtro ligado por padrão a
        // tela virou referência de conferência, e conferência não pode depender de dois
        // critérios que só por acaso coincidem.
        if (canceledValue) {
            filters.push({
                field: "cancellationDate",
                operation: canceledValue === 'N' ? SearchOperation.IS_NULL : SearchOperation.IS_NOT_NULL
            });
        }

        const payload = {
            filters,
            page: targetPage,
            size: targetSize,
            order: []
        };

        setShowLoading(true);
        axiosInstance.post(Api.sales.order + "/pageable", payload)
            .then((response) => {
                setPageable(response.data);
                setRows(response.data.content);
            })
            .catch(error => errorObjectAlert(error))
            .finally(() => {
                setShowLoading(false);
            });
    }, [startDate, endDate, customer, orderNumber, canceledValue, branch, selectedBranch, paymentMethod, productCategories, employeeFilter, page, size, t]);

    // Abre o modal de cancelamento
    const openReasonDialog = useCallback(() => {
        if (selectedRows.length === 0) {
            warnAlert(t('validation.required.select_latest_sale'));
            return;
        }

        setShowReasonModal(true);
    }, [selectedRows, t]);

    // Cancela a venda
    const onCancelSale = useCallback(() => {
        if (reasonCancellation === null) {
            warnAlert(t('validation.required.select_reason'));
            return;
        }

        const payload = {
            ids: selectedRows,
            reasonId: reasonCancellation.value
        };

        axiosInstance.patch(Api.sales.order + "/cancel", payload)
            .then(() => {
                setShowReasonModal(false);
                setSelectedRows([]);
                search();
            })
            // O backend devolve a causa real do 400 em response.data; errorAlert
            // exibiria apenas a mensagem genérica do Axios.
            .catch(error => errorObjectAlert(error));
    }, [reasonCancellation, selectedRows, search, t]);

    // Navega para o formulário de novo pedido
    const redirectToNew = useCallback(() => {
        navigate(MenuConfig.sales.order.route + "/new");
    }, [navigate]);

    // Abre modal de cupom não fiscal
    const openNonTaxCoupon = useCallback((order) => {
        setSelectedOrder(order);
        setShowNonTaxCoupon(true);
    }, []);

    // Abre modal de detalhe do pedido
    const openOrderDetail = useCallback((order) => {
        setSelectedOrder(order);
        setShowOrderDetail(true);
    }, []);

    // Handler para o formulário de filtros
    const handleSearchSubmit = useCallback((e) => {
        e?.preventDefault();
        if (page !== 0) {
            setPage(0);
            return;
        }

        search(0, size);
    }, [search]);

    // Handler para atalhos de data
    const handleQuickDateSelect = useCallback((startDate, endDate) => {
        setStartDate(startDate);
        setEndDate(endDate);
    }, []);

    // Handler para limpar filtros
    const handleClearFilters = useCallback(() => {
        setStartDate(new Date());
        setEndDate(new Date());
        setCustomer('');
        setCanceledValue('N');
        setOrderNumber('');
        setPaymentMethod(null);
        setProductCategories([]);
        setEmployeeFilter(null);
        if (selectedBranch === null) {
            setBranch(null);
        }
        if (page !== 0) {
            setPage(0);
            return;
        }
        setTimeout(() => search(0, size), 0);
    }, [page, search, selectedBranch, size]);

    // Busca automática ao mudar página, tamanho ou filial do contexto
    useEffect(() => {
        search();
    }, [page, size, selectedBranch]);

    // Sincroniza o campo branch com o contexto selectedBranch
    useEffect(() => {
        if (selectedBranch !== null) {
            setBranch({ value: selectedBranch.id, label: selectedBranch.name });
        } else {
            setBranch(null);
        }
    }, [selectedBranch]);

    return (
        <>
            <ModalPageEvent
                title={t('cancellation_reason')}
                show={showReasonModal}
                setShow={setShowReasonModal}
                child={
                    <>
                        <Row>
                            <Col className="col-md-12">
                                <FormGroup>
                                    <FormLabel>{t('reason')}</FormLabel>
                                    <ReasonSelect
                                        onChange={(value) => {
                                            setReasonCancellation(value)
                                        }} />
                                </FormGroup>
                            </Col>
                        </Row>
                    </>
                }
                size="md"
                btnOnClick={() => { onCancelSale() }}
                btnText={t("cancel_sale")}
            />
            <ModalPage
                title={t('non_tax_coupon')}
                show={showNonTaxCoupon}
                setShow={setShowNonTaxCoupon}
                child={
                    <NonTaxCouponGenerator order={selectedOrder} />
                }
                size="md"
            />
            <ModalPage
                title={t('order_detail')}
                show={showOrderDetail}
                setShow={setShowOrderDetail}
                child={
                    <OrderDetail order={selectedOrder} />
                }
                size="lg"
            />

            <FilterAccordion
                onSearch={handleSearchSubmit}
                onClear={handleClearFilters}
                showQuickDates={true}
                onQuickDateSelect={handleQuickDateSelect}
            >
                <Row className="g-3">
                    <Col md={2}>
                        <FormLabel>{t('start_date')}</FormLabel>
                        <InputDate onChange={setStartDate} selected={startDate} />
                    </Col>
                    <Col md={2}>
                        <FormLabel>{t('end_date')}</FormLabel>
                        <InputDate onChange={setEndDate} selected={endDate} />
                    </Col>
                    <Col md={3}>
                        <FormLabel>{t('customer')}</FormLabel>
                        <InputText value={customer} onChange={(e) => setCustomer(e.target.value)} />
                    </Col>
                    <Col md={2}>
                        <FormLabel>{t('order_number')}</FormLabel>
                        <InputText value={orderNumber} onChange={(e) => setOrderNumber(e.target.value)} />
                    </Col>
                    <Col md={3}>
                        <FormLabel>{t('payment_method')}</FormLabel>
                        <PaymentMethodSelect value={paymentMethod} onChange={setPaymentMethod} showCourtesy />
                    </Col>
                    <Col md={2}>
                        <FormLabel>{t('canceled')}</FormLabel>
                        <YesNoSelect value={canceled} onChange={(option) => setCanceledValue(option ? option.value : null)} isClearable={true} />
                    </Col>
                    <Col md={3}>
                        <FormLabel>{t('branch')}</FormLabel>
                        <BranchSelect value={branch} onChange={setBranch} isDisabled={selectedBranch !== null} />
                    </Col>
                    <Col md={7}>
                        <FormLabel>{t('categories')}</FormLabel>
                        <CategoryMultiSelect value={productCategories} onChange={(value) => setProductCategories(value || [])} />
                    </Col>
                    <Col md={5}>
                        <FormLabel>{t('employee')}</FormLabel>
                        <EmployeeSelect
                            branch={selectedBranch || branch}
                            value={employeeFilter}
                            onChange={setEmployeeFilter}
                            filterProvidesService={false}
                        />
                    </Col>
                </Row>
            </FilterAccordion>

            <Row className="row mb-3 mt-4">
                <Col md={12}>
                    <ButtonGroup>
                        <Dropdown>
                            <Dropdown.Toggle className="me-2" variant="primary">
                                {t('actions')}
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                                <Dropdown.Item onClick={() => redirectToNew()}><SaveIcon /> {t('new')}</Dropdown.Item>
                                <Dropdown.Item
                                    onClick={() => openOrderDetail(selectedOrderRows[0])}
                                    disabled={selectedOrderRows.length !== 1}
                                >
                                    <i className="fa fa-eye me-2"></i> {t('view_detail')}
                                </Dropdown.Item>
                                <Dropdown.Item
                                    onClick={() => openNonTaxCoupon(selectedOrderRows[0])}
                                    disabled={selectedOrderRows.length !== 1}
                                >
                                    <i className="fa fa-receipt me-2"></i> {t('non_tax_coupon')}
                                </Dropdown.Item>
                                <Dropdown.Item
                                    onClick={() => openReasonDialog()}
                                    disabled={selectedRows.length === 0}
                                >
                                    <BanIcon /> {t('cancel_sale')}
                                </Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                    </ButtonGroup>
                </Col>
            </Row>
            <GridCustomBase
                pageable={pageable}
                setPage={setPage}
                setSize={setSize}
                showLoading={showLoading}>

                <table className="table table-list i-table style-1 mb-0 border-0 order-history-table" id="guestTable-all3">
                    <thead>
                        <tr>
                            <th className="bg-none sorting_asc" >
                                <div className="form-check style-3">
                                    <input className="form-check-input" type="checkbox" onClick={() => checkboxFun('all')} />
                                </div>
                            </th>
                            <th>{t("order_number")}</th>
                            <th>{t("detail")}</th>
                            <th>{t("date")}</th>
                            <th>{t("total")}</th>
                            <th>{t("payment_method")}</th>
                            <th>{t("customer")}</th>
                            <th className="text-center">{t("status")}</th>
                            <th className="bg-none"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, index) => (
                            <tr key={index} className={"align-middle h6 order-history-table__row " + (row.cancellationDate !== null ? 'order-history-table__row--cancelled' : '')}>
                                <td className='item-history'>
                                    <div className="form-check">
                                        {
                                            row.cancellationDate !== null ?
                                                <></>
                                                :
                                                <input className="form-check-input" type="checkbox" value={row.id} onClick={() => checkboxFun()} />
                                        }

                                    </div>
                                </td>
                                <td>
                                    <strong className="text-primary">#{row.number}</strong>
                                </td>
                                <td>
                                    <div className="order-history-table__item" style={{ fontSize: '0.85rem', fontWeight: 500 }}>
                                        {row.items.slice(0, 2).map(i => `${i.qty}× ${i.product.name}`).join(', ')}
                                        {row.items.length > 2 && (
                                            <span className="text-muted"> +{row.items.length - 2}</span>
                                        )}
                                    </div>
                                    {row.employee && (
                                        <div className="mt-1 order-history-table__employee" style={{ fontSize: '0.75rem' }}>
                                            <i className="fa-regular fa-id-badge me-1"></i>{row.employee.fullName}
                                        </div>
                                    )}
                                </td>
                                <td>
                                    <i className="fa fa-clock text-muted me-1"></i>
                                    {formatDateTime(row.requestDate)}
                                </td>
                                <td>
                                    <i className="fa fa-money-bill-wave text-success me-1"></i>
                                    <strong className="text-success" style={{ fontSize: '1.1em' }}>{t('currency_symbol')} {convertDoubleToStringPTBR(row.total)}</strong>
                                </td>
                                <td>
                                    <Badge 
                                        bg={row.paymentMethod === 'MONEY' ? 'success' : row.paymentMethod === 'CREDIT_CARD' ? 'primary' : row.paymentMethod === 'DEBIT_CARD' ? 'info' : row.paymentMethod === 'COURTESY' ? 'warning' : 'secondary'}
                                        className="badge-lg"
                                    >
                                        <i className={`fa ${row.paymentMethod === 'MONEY' ? 'fa-money-bill' : row.paymentMethod === 'PIX' ? 'fa-qrcode' : row.paymentMethod === 'COURTESY' ? 'fa-gift' : 'fa-credit-card'} me-1`}></i>
                                        {t(row.paymentMethod.toLowerCase())}
                                    </Badge>
                                </td>
                                <td>
                                    {row.customer === null ? (
                                        <span className="text-muted">-</span>
                                    ) : (
                                        <span style={{ fontWeight: 500 }}>{row.customer.fullName}</span>
                                    )}
                                </td>
                                <td>
                                    {row.cancellationDate !== null ? (
                                        <Badge bg="danger" className="badge-lg">
                                            <i className="fa fa-times-circle me-1"></i>
                                            {t('cancelled')}
                                        </Badge>
                                    ) : (
                                        <Badge bg="success" className="badge-lg">
                                            <i className="fa fa-check-circle me-1"></i>
                                            {t('active')}
                                        </Badge>
                                    )}
                                </td>
                                <td>
                                    <Dropdown className="">
                                        <Dropdown.Toggle as="div" className="btn-link i-false order-history-table__row-action" style={{ cursor: 'pointer' }}>
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M11 12C11 12.5523 11.4477 13 12 13C12.5523 13 13 12.5523 13 12C13 11.4477 12.5523 11 12 11C11.4477 11 11 11.4477 11 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                <path d="M18 12C18 12.5523 18.4477 13 19 13C19.5523 13 20 12.5523 20 12C20 11.4477 19.5523 11 19 11C18.4477 11 18 11.4477 18 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                <path d="M4 12C4 12.5523 4.44772 13 5 13C5.55228 13 6 12.5523 6 12C6 11.4477 5.55228 11 5 11C4.44772 11 4 11.4477 4 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        </Dropdown.Toggle>
                                        <Dropdown.Menu >
                                            <Dropdown.Item onClick={() => openOrderDetail(row)}>
                                                <i className="fa fa-eye me-2"></i> {t('view_detail')}
                                            </Dropdown.Item>
                                            <Dropdown.Item onClick={() => openNonTaxCoupon(row)}>
                                                <i className="fa fa-receipt me-2"></i> {t('non_tax_coupon')}
                                            </Dropdown.Item>
                                            {/*<Dropdown.Item>Delete</Dropdown.Item>*/}
                                        </Dropdown.Menu>
                                    </Dropdown>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </GridCustomBase>
        </>
    );
}

export default OrderHistory;
